<template>
  <b-modal
    id="weekly-sheet-detail-modal"
    title="Timesheet status"
    size="lg"
    centered
    hide-footer
    modal-class="timesheet-modal"
    @show="load"
    @hidden="sheet = null"
  >
    <div v-if="loading" class="text-center py-3">
      <b-spinner small /> Loading…
    </div>
    <b-alert v-else-if="error" show variant="danger">
      {{ error }}
      <a href="#" class="alert-link ml-1" @click.prevent="load">Try again</a>
    </b-alert>

    <template v-else-if="sheet">
      <h3 class="wd-title">{{ sheet.project_name }}</h3>
      <p class="text-muted mb-2">
        Week of {{ formatDate(sheet.week_start) }} ·
        {{ formatHours(total) || '0h' }}
      </p>
      <b-badge :variant="statusVariant" class="wd-badge" data-test="status">
        {{ statusLabel }}
      </b-badge>

      <div v-if="req" class="wd-card" data-test="request">
        <div class="wd-line">
          <span>{{
            sheet.status === 'draft' ? 'Last sent to' : 'Sent to'
          }}</span>
          <span>{{
            [req.supervisor.name, req.supervisor.company]
              .filter(Boolean)
              .join(', ')
          }}</span>
        </div>
        <div class="wd-line">
          <span>Email</span><span>{{ req.supervisor.email }}</span>
        </div>
        <div v-if="req.sent_at" class="wd-line">
          <span>Sent</span><span>{{ formatDateTime(req.sent_at) }}</span>
        </div>
        <div v-if="pending && req.expires_at" class="wd-line">
          <span>{{ expired ? 'Link expired' : 'Link expires' }}</span>
          <span>{{ formatDateTime(req.expires_at) }}</span>
        </div>
        <div v-if="req.decided_at" class="wd-line">
          <span>Decided</span><span>{{ formatDateTime(req.decided_at) }}</span>
        </div>
        <div v-if="req.comment" class="wd-line">
          <span>{{ rejected ? 'Reason' : 'Comment' }}</span>
          <span>{{ req.comment }}</span>
        </div>
      </div>

      <div v-if="sheet.admin_approval" class="wd-card" data-test="admin">
        <div class="wd-line">
          <span>Approved by</span>
          <span>{{ sheet.admin_approval.by || 'Company admin' }}</span>
        </div>
        <div v-if="sheet.admin_approval.at" class="wd-line">
          <span>When</span>
          <span>{{ formatDateTime(sheet.admin_approval.at) }}</span>
        </div>
        <div v-if="sheet.admin_approval.reason" class="wd-line">
          <span>Reason</span><span>{{ sheet.admin_approval.reason }}</span>
        </div>
      </div>

      <div v-if="sheet.finalised_at" class="wd-card" data-test="finalised">
        <div class="wd-line">
          <span>Finalised</span>
          <span>{{ formatDateTime(sheet.finalised_at) }}</span>
        </div>
      </div>

      <b-alert v-if="actionError" show variant="danger" class="mt-3">
        {{ actionError }}
      </b-alert>

      <div v-if="pending" class="wd-actions">
        <b-button
          variant="outline-primary"
          :disabled="busy"
          data-test="resend"
          @click="resend"
        >
          {{ expired ? 'Send a new link' : 'Resend link' }}
        </b-button>
        <b-button
          variant="outline-danger"
          :disabled="busy"
          data-test="withdraw"
          @click="withdraw"
        >
          Withdraw
        </b-button>
      </div>
      <div v-if="rejected" class="wd-actions">
        <b-button variant="primary" data-test="amend" @click="amend">
          Amend and resubmit
        </b-button>
      </div>

      <template v-if="versions.length">
        <h4 class="wd-h4">History</h4>
        <div
          v-for="v in versions"
          :key="v.number"
          class="wd-card"
          :data-test="`version-${v.number}`"
        >
          <strong>v{{ v.number }} · {{ kindLabel(v.kind) }}</strong>
          <div class="text-muted small">
            {{ [formatDateTime(v.at), v.by].filter(Boolean).join(' · ') }}
          </div>
          <div v-if="v.total_minutes !== null && v.total_minutes !== undefined">
            Total {{ formatHours(v.total_minutes) || '0h' }}
          </div>
          <div v-if="v.note">{{ v.note }}</div>
          <div
            v-for="d in v.amended_days || []"
            :key="d.date"
            class="wd-amended"
          >
            {{ formatDate(d.date) }}:
            {{ span(d.original_start, d.original_finish) }}
            ({{ formatHours(d.original_minutes) || '0h' }}) →
            {{ span(d.amended_start, d.amended_finish) }}
            ({{ formatHours(d.amended_minutes) || '0h' }})
          </div>
        </div>
      </template>
    </template>
  </b-modal>
</template>

<script>
import dayjs from 'dayjs'
import { formatHours } from '@/utils/timesheetHelpers'
import {
  ownDays,
  versionKindLabel,
  weekTotalMinutes,
  weeklyStatusLabel,
  weeklyStatusVariant,
} from '@/utils/myWeekSheet'

const apiMessage = (e, fallback) =>
  (e && e.response && e.response.data && e.response.data.message) || fallback

// One week's status and history, as in the app's WeeklySheetDetailScreen: who
// it went to, the link, any decision or admin approval, and each version
// (with the supervisor's changes, day by day). A waiting week can have its
// link resent or be withdrawn; a rejected one goes back to Submit to amend.
export default {
  props: {
    sheetId: { type: Number, default: null },
  },

  data() {
    return {
      sheet: null,
      loading: false,
      error: null,
      busy: false,
      actionError: null,
    }
  },

  computed: {
    req() {
      return this.sheet && this.sheet.external_request
    },
    total() {
      return weekTotalMinutes(ownDays(this.sheet))
    },
    statusLabel() {
      return weeklyStatusLabel(this.sheet)
    },
    statusVariant() {
      return weeklyStatusVariant(this.statusLabel)
    },
    pending() {
      return !!this.sheet && this.sheet.status === 'submitted'
    },
    rejected() {
      return !!this.sheet && this.sheet.status === 'rejected'
    },
    expired() {
      return (
        !!this.req &&
        (this.req.state === 'expired' ||
          (!!this.req.expires_at &&
            dayjs(this.req.expires_at).isBefore(dayjs())))
      )
    },
    versions() {
      return ((this.sheet && this.sheet.versions) || []).slice().reverse()
    },
  },

  methods: {
    formatHours,
    kindLabel: versionKindLabel,

    formatDate(date) {
      return dayjs(date).format('ddd D MMM')
    },

    formatDateTime(iso) {
      return iso ? dayjs(iso).format('D MMM YYYY, h:mm a') : ''
    },

    span(start, finish) {
      return start && finish ? `${start} to ${finish}` : 'No hours'
    },

    async load() {
      if (!this.sheetId) return
      this.loading = true
      this.error = null
      this.actionError = null
      try {
        this.sheet = await this.$store.dispatch(
          'timesheet/myWeekFetch',
          this.sheetId,
        )
      } catch (e) {
        this.error = apiMessage(e, 'Could not load this timesheet.')
      } finally {
        this.loading = false
      }
    },

    async resend() {
      this.busy = true
      this.actionError = null
      try {
        await this.$store.dispatch('timesheet/myWeekResend', this.sheet.id)
        await this.load()
        this.$bvToast.toast('A new link was sent to your supervisor.', {
          variant: 'success',
          title: 'Sent',
          solid: true,
        })
        this.$emit('changed')
      } catch (e) {
        this.actionError = apiMessage(e, 'Could not send a new link.')
      } finally {
        this.busy = false
      }
    },

    async withdraw() {
      const ok = await this.$bvModal.msgBoxConfirm(
        'This takes the week back to draft and cancels the link sent to your supervisor.',
        {
          title: 'Withdraw timesheet?',
          okTitle: 'Withdraw',
          okVariant: 'danger',
          centered: true,
          modalClass: 'confirm-modal',
        },
      )
      if (!ok) return
      this.busy = true
      this.actionError = null
      try {
        await this.$store.dispatch('timesheet/myWeekWithdraw', this.sheet.id)
        await this.load()
        this.$bvToast.toast('Timesheet withdrawn.', {
          variant: 'success',
          title: 'Withdrawn',
          solid: true,
        })
        this.$emit('changed')
      } catch (e) {
        this.actionError = apiMessage(e, 'Could not withdraw the timesheet.')
      } finally {
        this.busy = false
      }
    },

    amend() {
      this.$bvModal.hide('weekly-sheet-detail-modal')
      this.$emit('amend', this.sheet)
    },
  },
}
</script>

<style lang="scss" scoped>
.wd-title {
  font-size: 1.15rem;
  font-weight: 700;
  margin-bottom: 2px;
}
.wd-badge {
  white-space: normal;
}
.wd-card {
  border: 1px solid #e4e7eb;
  border-radius: 10px;
  padding: 10px 12px;
  margin-top: 12px;
}
.wd-line {
  display: flex;
  gap: 12px;
  padding: 3px 0;
}
.wd-line > span:first-child {
  flex: 0 0 110px;
  color: #6c757d;
}
.wd-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
}
.wd-h4 {
  font-size: 1rem;
  font-weight: 700;
  margin-top: 24px;
  margin-bottom: 0;
}
.wd-amended {
  font-size: 0.9rem;
  margin-top: 6px;
}
</style>
